// HFLedger/Dashboard.js

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './Dashboard.css';

function Dashboard({ user, language, SERVER_ROOT, settings }) {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({
    income: 0,
    expense: 0,
    balance: 0
  });
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [budgetProgress, setBudgetProgress] = useState([]);
  const [error, setError] = useState(null);

  // 데이터 불러오기
  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!user) return;
      
      setLoading(true);
      try {
        // 1. 이번 달 수입/지출 요약
        const currentDate = new Date();
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth() + 1;
        
        const summaryResponse = await axios.get(
          `${SERVER_ROOT}/api/hfl/transactions/summary/monthly?user_id=${user.uid}&year=${year}&month=${month}`
        );
        
        if (summaryResponse.data && summaryResponse.data.length > 0) {
          const monthlySummary = summaryResponse.data[0];
          const income = monthlySummary.currencies[settings.default_currency]?.income || 0;
          const expense = monthlySummary.currencies[settings.default_currency]?.expense || 0;
          
          setSummary({
            income,
            expense,
            balance: income - expense
          });
        }
        
        // 2. 최근 거래 내역
        const transactionsResponse = await axios.get(
          `${SERVER_ROOT}/api/hfl/transactions?user_id=${user.uid}&limit=5`
        );
        
        setRecentTransactions(transactionsResponse.data);
        
        // 3. 계좌 정보
        const accountsResponse = await axios.get(
          `${SERVER_ROOT}/api/hfl/accounts/${user.uid}`
        );
        
        setAccounts(accountsResponse.data);
        
        // 4. 진행 중인 예산
        const budgetsResponse = await axios.get(
          `${SERVER_ROOT}/api/hfl/budgets/${user.uid}?period_type=monthly`
        );
        
        // 각 예산에 대해 진행 상황 조회
        const budgetProgressPromises = budgetsResponse.data.map(budget => 
          axios.get(`${SERVER_ROOT}/api/hfl/budgets/${budget.budget_id}/progress`)
        );
        
        const budgetProgressResponses = await Promise.all(budgetProgressPromises);
        const budgetProgressData = budgetProgressResponses.map(response => response.data);
        
        setBudgetProgress(budgetProgressData);
        
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError(err.message || '데이터를 불러오는 중 오류가 발생했습니다.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchDashboardData();
  }, [user, SERVER_ROOT, settings.default_currency]);

  // 통화 포맷팅 헬퍼 함수
  const formatCurrency = (amount, currency = settings.default_currency) => {
    return new Intl.NumberFormat(language === 'KR' ? 'ko-KR' : 'en-US', {
      style: 'currency',
      currency: currency,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // 날짜 포맷팅 헬퍼 함수
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat(language === 'KR' ? 'ko-KR' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="dashboard-spinner"></div>
        <p>{language === 'KR' ? '대시보드 로딩 중...' : 'Loading dashboard...'}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-error">
        <h3>{language === 'KR' ? '데이터 로딩 오류' : 'Error Loading Data'}</h3>
        <p>{error}</p>
        <button 
          onClick={() => window.location.reload()}
          className="dashboard-button"
        >
          {language === 'KR' ? '새로고침' : 'Refresh'}
        </button>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {/* 월별 요약 */}
      <div className="dashboard-summary">
        <h2>{language === 'KR' ? '이번 달 요약' : 'Monthly Summary'}</h2>
        <div className="dashboard-summary-cards">
          <div className="summary-card income" style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-evenly'}}>
            <h3>{language === 'KR' ? '수입' : 'Income'}</h3>
            <p className="amount">{formatCurrency(summary.income)}</p>
          </div>
          
          <div className="summary-card expense" style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-evenly'}}>
            <h3>{language === 'KR' ? '지출' : 'Expense'}</h3>
            <p className="amount">{formatCurrency(summary.expense)}</p>
          </div>
          
          <div className="summary-card balance" style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-evenly'}}>
            <h3>{language === 'KR' ? '잔액' : 'Balance'}</h3>
            <p className="amount">{formatCurrency(summary.balance)}</p>
          </div>
        </div>
      </div>
      
      {/* 계좌 정보 */}
      <div className="dashboard-accounts">
        <h2>{language === 'KR' ? '내 계좌' : 'My Accounts'}</h2>
        <div className="accounts-list">
          {accounts.length === 0 ? (
            <p className="no-data">
              {language === 'KR' ? '등록된 계좌가 없습니다.' : 'No accounts registered.'}
            </p>
          ) : (
            accounts.map(account => (
              <div className="account-card" key={account.account_id}>
                <div className="account-name-type">
                  <h3>{account.account_name}</h3>
                  <span className="account-type">{account.account_type}</span>
                </div>
                <p className="account-balance">
                  {formatCurrency(account.current_balance, account.currency)}
                </p>
              </div>
            ))
          )}
        </div>
      </div>
      
      {/* 최근 거래 */}
      <div className="dashboard-recent-transactions">
        <h2>{language === 'KR' ? '최근 거래' : 'Recent Transactions'}</h2>
        {recentTransactions.length === 0 ? (
          <p className="no-data">
            {language === 'KR' ? '최근 거래 내역이 없습니다.' : 'No recent transactions.'}
          </p>
        ) : (
          <div className="transactions-list">
            {recentTransactions.map(transaction => (
              <div 
                className={`transaction-item ${transaction.type}`} 
                key={transaction.transaction_id}
              >
                <div className="transaction-date-category" style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-evenly'}}>
                  <span className="transaction-date">{formatDate(transaction.transaction_date)}</span>
                  <span 
                    className="transaction-category" 
                    style={{ backgroundColor: transaction.category_color }}
                  >
                    {transaction.category_name}
                  </span>
                </div>
                <div className="transaction-description">
                  <p>{transaction.description || (language === 'KR' ? '설명 없음' : 'No description')}</p>
                </div>
                <div className="transaction-amount">
                  <span className={transaction.type === 'income' ? 'positive' : 'negative'}>
                    {transaction.type === 'income' ? '+' : '-'} 
                    {formatCurrency(transaction.amount, transaction.currency)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )} 
      </div>
      
      {/* 예산 진행 상황 */}
      {/* <div className="dashboard-budget-progress">
        <h2>{language === 'KR' ? '예산 진행 상황' : 'Budget Progress'}</h2>
        {budgetProgress.length === 0 ? (
          <p className="no-data">
            {language === 'KR' ? '설정된 예산이 없습니다.' : 'No budget set.'}
          </p>
        ) : (
          <div className="budget-progress-list">
            {budgetProgress.map(budget => (
              <div className="budget-item" key={budget.budget_id}>
                <div className="budget-header">
                  <h3>{budget.category_name || (language === 'KR' ? '전체 예산' : 'Total Budget')}</h3>
                  <span className="budget-dates">
                    {formatDate(budget.start_date)} - {formatDate(budget.end_date)}
                  </span>
                </div>
                <div className="budget-details">
                  <div className="budget-amounts">
                    <span className="spent">
                      {language === 'KR' ? '지출: ' : 'Spent: '}
                      {formatCurrency(budget.total_spent, budget.currency)}
                    </span>
                    <span className="budget">
                      {language === 'KR' ? '예산: ' : 'Budget: '}
                      {formatCurrency(budget.amount, budget.currency)}
                    </span>
                  </div>
                  <div className="budget-progress-bar">
                    <div 
                      className={`progress ${budget.progress_percentage > 85 ? 'warning' : (budget.progress_percentage > 100 ? 'danger' : '')}`}
                      style={{ width: `${Math.min(budget.progress_percentage, 100)}%` }}
                    ></div>
                  </div>
                  <div className="budget-percentage">
                    {Math.round(budget.progress_percentage)}%
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div> */}
    </div>
  );
}

export default Dashboard;